import type { FocusTrap, FocusTrapConfig } from "../types";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "button:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
  "audio[controls]",
  "video[controls]",
  '[contenteditable]:not([contenteditable="false"])',
].join(",");

const focusableElementsCache = new WeakMap<HTMLElement, { elements: HTMLElement[]; timestamp: number }>();
const CACHE_TIMEOUT = 500; //* Cache for 500ms to balance performance vs accuracy

const liveRegionCache = new Map<string, HTMLElement>();

const focusTrapCache = new WeakMap<HTMLElement, FocusTrap>();

export function getFocusableElements(container: HTMLElement): HTMLElement[] {
  const cached = focusableElementsCache.get(container);
  const now = Date.now();

  if (cached && now - cached.timestamp < CACHE_TIMEOUT) {
    return cached.elements;
  }

  const elements = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

  const filteredElements = elements.filter((el) => {
    if (el.offsetParent === null) return false;

    if (el.getAttribute("aria-hidden") === "true") return false;

    return true;
  });

  focusableElementsCache.set(container, { elements: filteredElements, timestamp: now });

  return filteredElements;
}

export function createFocusTrap(config: FocusTrapConfig): FocusTrap {
  const { element, allowEscape = true, initialFocus, returnFocus, onActivate, onDeactivate } = config;

  const cached = focusTrapCache.get(element);
  if (cached && cached.isActive && !cached.isActive()) {
    return cached;
  }

  let active = false;
  let previousFocus: Element | null = null;
  let boundHandleKeydown: ((e: KeyboardEvent) => void) | null = null;

  const activate = (): void => {
    if (active) return;

    previousFocus = document.activeElement;

    const focusable = getFocusableElements(element);

    if (focusable.length === 0) {
      console.warn("Focus trap activated on element with no focusable children");
      return;
    }

    const toFocus = initialFocus || focusable[0];
    toFocus.focus();

    boundHandleKeydown = (e: KeyboardEvent) => handleKeydown(e, element, allowEscape, deactivate);
    element.addEventListener("keydown", boundHandleKeydown);

    active = true;
    onActivate?.();
  };

  const deactivate = (): void => {
    if (!active) return;

    if (boundHandleKeydown) {
      element.removeEventListener("keydown", boundHandleKeydown);
      boundHandleKeydown = null;
    }

    const toFocus = returnFocus || previousFocus;
    if (toFocus && toFocus instanceof HTMLElement) {
      toFocus.focus();
    }

    active = false;
    onDeactivate?.();
  };

  const isActive = (): boolean => active;

  const trap: FocusTrap = {
    activate,
    deactivate,
    isActive,
  };

  focusTrapCache.set(element, trap);

  return trap;
}

function handleKeydown(e: KeyboardEvent, container: HTMLElement, allowEscape: boolean, deactivate: () => void): void {
  if (allowEscape && e.key === "Escape") {
    e.preventDefault();
    deactivate();
    return;
  }

  if (e.key === "Tab") {
    const focusable = getFocusableElements(container);

    if (focusable.length === 0) return;

    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }
}

export function moveFocusTo(element: HTMLElement, announce?: string): void {
  element.focus();

  if (announce) {
    announceToScreenReader(announce);
  }
}

export function getNextFocusable(current: HTMLElement, container?: HTMLElement): HTMLElement | null {
  const root = container || document.body;
  const focusable = getFocusableElements(root);
  const currentIndex = focusable.indexOf(current);

  if (currentIndex === -1 || currentIndex === focusable.length - 1) {
    return focusable[0] || null;
  }

  return focusable[currentIndex + 1];
}

export function getPreviousFocusable(current: HTMLElement, container?: HTMLElement): HTMLElement | null {
  const root = container || document.body;
  const focusable = getFocusableElements(root);
  const currentIndex = focusable.indexOf(current);

  if (currentIndex === -1 || currentIndex === 0) {
    return focusable[focusable.length - 1] || null;
  }

  return focusable[currentIndex - 1];
}

export function isFocusable(element: HTMLElement): boolean {
  if (!element.matches(FOCUSABLE_SELECTOR)) {
    return false;
  }

  if (element.offsetParent === null) {
    return false;
  }

  if (element.hasAttribute("disabled")) {
    return false;
  }

  if (element.getAttribute("aria-hidden") === "true") {
    return false;
  }

  return true;
}

export function ensureFocusVisible(element: HTMLElement): () => void {
  element.classList.add("focus-visible-enhanced");

  const handleFocus = () => {
    element.setAttribute("data-focus-visible", "true");
  };

  const handleBlur = () => {
    element.removeAttribute("data-focus-visible");
  };

  element.addEventListener("focus", handleFocus);
  element.addEventListener("blur", handleBlur);

  return () => {
    element.removeEventListener("focus", handleFocus);
    element.removeEventListener("blur", handleBlur);
    element.classList.remove("focus-visible-enhanced");
  };
}

function announceToScreenReader(message: string, priority: "polite" | "assertive" = "polite"): void {
  let liveRegion = liveRegionCache.get("announcer");

  if (!liveRegion || !document.body.contains(liveRegion)) {
    liveRegion = document.getElementById("hotkeys-sr-announcer") as HTMLDivElement;

    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "hotkeys-sr-announcer";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", priority);
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.className = "sr-only";
      liveRegion.style.cssText = `
        position: absolute;
        left: -10000px;
        width: 1px;
        height: 1px;
        overflow: hidden;
      `;
      document.body.appendChild(liveRegion);
    }

    liveRegionCache.set("announcer", liveRegion);
  }

  liveRegion.textContent = "";
  setTimeout(() => {
    liveRegion!.textContent = message;
  }, 100);
}

export class FocusScope {
  private container: HTMLElement;
  private onEscape?: () => void;

  constructor(container: HTMLElement, onEscape?: () => void) {
    this.container = container;
    this.onEscape = onEscape;
  }

  activate(): void {
    const focusable = getFocusableElements(this.container);
    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }

  handleKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape" && this.onEscape) {
      e.preventDefault();
      this.onEscape();
      return;
    }

    if (e.key === "Tab") {
      this.handleTab(e);
    }
  }

  private handleTab(e: KeyboardEvent): void {
    const focusable = getFocusableElements(this.container);
    if (focusable.length === 0) return;

    const currentIndex = focusable.findIndex((el) => el === document.activeElement);

    if (currentIndex === -1) {
      e.preventDefault();
      focusable[0].focus();
      return;
    }

    if (e.shiftKey) {
      if (currentIndex === 0) {
        e.preventDefault();
        focusable[focusable.length - 1].focus();
      }
    } else {
      if (currentIndex === focusable.length - 1) {
        e.preventDefault();
        focusable[0].focus();
      }
    }
  }
}

export const focusManagementPerformance = {
  clearCache(): void {
    // NOTE: WeakMap doesn't have clear() method - automatically garbage collected when references are removed
    liveRegionCache.clear();
  },

  getCacheStats(): {
    liveRegions: number;
    totalMemoryEstimate: string;
  } {
    //* Approximate memory per live region element
    const estimate = liveRegionCache.size * 100;

    return {
      liveRegions: liveRegionCache.size,
      totalMemoryEstimate: `~${(estimate / 1024).toFixed(1)}KB`,
    };
  },

  preloadCommonElements(): void {
    getFocusableElements(document.body);

    const main = document.querySelector("main");
    if (main) getFocusableElements(main);

    const header = document.querySelector("header");
    if (header) getFocusableElements(header);

    const nav = document.querySelector("nav");
    if (nav) getFocusableElements(nav);
  },
};
